import { useState } from 'react';
import { Icon } from '@iconify/react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/shadcn/alert-dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/shadcn/sheet';
import showToast from '@/components/ui/Toast';
import {
  durationLabel,
  formatDayTitle,
  formatRupiah,
  formatTimeRange,
  parseDateKey,
  whatsAppLink,
} from '@/components/admin/booking/bookingCalendarUtils';
import type { DetailTarget } from '@/components/admin/booking/useBookingCalendar';
import { useDesktopSheet } from '@/components/admin/booking/useDesktopSheet';

interface BookingDetailDrawerProps {
  target: DetailTarget | null;
  onClose: () => void;
  onPaid: (orderNumber: string) => Promise<void>;
  onCancel: (invoiceNumber: string) => Promise<void>;
}

function Row({ label, value, emphasize }: { label: string; value: string; emphasize?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-[#E0E6ED] py-3 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className={emphasize ? 'font-semibold text-[#FC480D]' : 'text-right font-medium text-[#0E1726]'}>
        {value}
      </span>
    </div>
  );
}

export default function BookingDetailDrawer({
  target,
  onClose,
  onPaid,
  onCancel,
}: BookingDetailDrawerProps) {
  const desktop = useDesktopSheet();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const slot = target?.slot;
  const paid = slot?.payment_status === 'paid';

  const contact = () => {
    if (!slot?.phone) {
      showToast.error('Nomor WhatsApp tidak tersedia');
      return;
    }
    window.open(whatsAppLink(slot.phone), '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <Sheet open={Boolean(target)} onOpenChange={(open) => { if (!open) onClose(); }}>
        <SheetContent
          side={desktop ? 'right' : 'bottom'}
          className={desktop ? 'w-full overflow-y-auto sm:max-w-md p-6' : 'h-[92vh] overflow-y-auto rounded-t-2xl p-6'}
        >
          <SheetHeader className="border-b border-[#E0E6ED] pb-3">
            <SheetTitle className="text-lg font-semibold text-[#0E1726]">Detail Booking</SheetTitle>
          </SheetHeader>
          {slot && target && (
            <div className="mt-5 space-y-6">
              {/* Header Info */}
              <div className="flex items-start justify-between gap-3 rounded-xl bg-[#F5F6F7] p-4">
                <div>
                  <p className="text-xs font-semibold text-gray-400">{slot.order_number}</p>
                  <p className="mt-0.5 text-base font-semibold text-[#0E1726]">{slot.customer_name || 'Booking'}</p>
                  <p className="text-xs text-gray-500">{slot.phone || '-'}</p>
                </div>
                <span
                  className={
                    paid
                      ? 'inline-flex items-center rounded-full border border-[#0BCF46] bg-[#ECFDF3] px-3 py-1 text-xs font-semibold text-[#027A48]'
                      : slot.payment_status === 'dp_paid' || slot.payment_status?.includes('dp')
                      ? 'inline-flex items-center rounded-full border border-[#FC480D] bg-[#FFF5EE] px-3 py-1 text-xs font-semibold text-[#FC480D]'
                      : 'inline-flex items-center rounded-full border border-[#E7515A] bg-[#FEF2F2] px-3 py-1 text-xs font-semibold text-[#B91C1C]'
                  }
                >
                  {paid ? 'PAID' : (slot.payment_status === 'dp_paid' || slot.payment_status?.includes('dp')) ? 'DP 50%' : 'PENDING'}
                </span>
              </div>

              {/* Data Table */}
              <div className="rounded-xl border border-[#E0E6ED] px-4">
                <Row label="Lapangan" value={target.field.name} />
                <Row label="Jenis olahraga" value={target.field.sport_name || '-'} />
                <Row label="Tanggal" value={formatDayTitle(parseDateKey(slot.date))} />
                <Row label="Jam" value={formatTimeRange(slot.start, slot.end)} />
                <Row label="Durasi" value={durationLabel(slot.start, slot.end)} />
                <Row label="Total" value={formatRupiah(slot.total || slot.price)} />
                <Row label="Dibayar" value={formatRupiah(slot.paid_amount || 0)} />
                <Row label="Sisa tagihan" value={formatRupiah(paid ? 0 : slot.remaining_amount || 0)} emphasize />
              </div>

              {/* Action Buttons */}
              <div className="space-y-2.5">
                <div className={paid ? '' : 'grid grid-cols-2 gap-2.5'}>
                  <button
                    type="button"
                    className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-full border border-[#E0E6ED] bg-white text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
                    onClick={contact}
                  >
                    <Icon icon="ph:whatsapp-logo-bold" className="h-4 w-4 text-[#0BCF46]" />
                    Hubungi
                  </button>
                  {!paid && (
                    <button
                      type="button"
                      disabled={busy}
                      className="inline-flex h-10 w-full items-center justify-center rounded-full bg-[#FC480D] text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#E8490F] disabled:opacity-50"
                      onClick={() => {
                        setBusy(true);
                        void onPaid(slot.order_number || '').finally(() => setBusy(false));
                      }}
                    >
                      Tandai Lunas
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  className="inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-full border border-red-200 bg-[#FFF5EE] text-sm font-semibold text-[#E7515A] transition-colors hover:bg-red-100/60"
                  onClick={() => setConfirm(true)}
                >
                  <Icon icon="ph:trash-bold" className="h-4 w-4" />
                  Batalkan booking
                </button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="max-w-md rounded-2xl border border-[#E0E6ED] bg-white p-6 shadow-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-semibold text-[#0E1726]">Batalkan booking ini?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-gray-600">
              Booking akan dibatalkan dan slot kembali mengikuti mode ketersediaan lapangan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4 flex items-center justify-end gap-2">
            <AlertDialogCancel className="h-10 rounded-full border border-[#E0E6ED] bg-white px-5 text-sm font-semibold text-gray-700 hover:bg-gray-50">
              Tidak
            </AlertDialogCancel>
            <AlertDialogAction
              className="h-10 rounded-full bg-[#E7515A] px-5 text-sm font-semibold text-white hover:bg-red-600"
              onClick={() => {
                if (slot?.invoice_number) void onCancel(slot.invoice_number);
                else showToast.error('Nomor invoice tidak tersedia');
              }}
            >
              Batalkan
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
